import { Directive, Input, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep, isNil } from 'lodash';
import { MbsPopupType, MbsSize, ModalService, Toast, ToastService, TreeComponent, TreeElement } from 'mbs-ui-kit';
import { windowsInvalidPath } from 'mbs-ui-shared@constants';
import { DeepSyncCodes, ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { unidentifiedErrorText } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import {
  DeepSyncEnum,
  ItemTypeEnum,
  MyTreeElements,
  TreeBunchesIcons,
  TreeRestorePointsIconPath
} from 'mbs-ui-shared@models/backup/storages-type';
import { PasswordModalComponent } from 'mbs-ui-shared@modules/password-modal/password-modal.component';
import { cobbledDateFormat, dateTimePointsFormat, dateTimePointsReg } from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import { ExtendedTreeElement, GenerateTreeDataSettings, StepsHelpers } from 'wizards/helpers/steps-helpers';
import { RestorePointItem } from 'wizards/models/restore-point-models';
import { TreeIconPath } from 'wizards/models/what-backup-tree-model';
import { WhatRunAfterPassword } from 'wizards/models/what-run-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { GetBackupContentParams, ParamsForDeepSync, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

@Directive()
export class BaseForRestoreSourceOrDisksStep<T> extends StepBase<T> {
  mainService: RemoteManagementWizardsService;
  modal = inject(ModalService);
  stepService = inject(WizardStepsService);
  toastService = inject(ToastService);

  @Input() bunchId: string;
  @Input() computerName: string;
  @Input() manually: boolean;
  @Input() isVirtual: boolean;
  @Input() resultPoint: RestorePointItem;
  @Input() storageId: string = null;

  isDisks: boolean;

  public readonly MbsSize = MbsSize;
  private readonly passwordErrorCodes = [ErrorCode.InvalidDecryptionPassword, ErrorCode.InvalidBitlockerPassword];

  public loadingTree = false;
  public separator = ')-(';
  public allowedRecovery = false;
  public passwordInvalid = false;
  public passwordsForBunches: { [key: string]: string } = {};
  public validVersionForGetDisks = false;

  public treeData: TreeElement[] = [];
  public needAllCheckChildren: { [key: string]: boolean } = {};
  public treeParams: GetBackupContentParams = {
    agentType: 'backup',
    commandType: 'GetBackupContent',
    params: {
      ConnectionId: this.storageId,
      RestoreSourcePrefix: this.mainService.compName,
      BunchId: 'Image Based',
      RestorePointDateUtc: '',
      path: '',
      offset: 0,
      limit: 300,
      order: 'DisplayNameAsc'
    }
  };

  private currentSyncData: { progress: number; deepSync: DeepSyncEnum; progressMessage: string };

  @ViewChild(TreeComponent, { static: false }) treeComponent: TreeComponent;

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;

    if (this.mainService.sessionId) this.treeParams.params.SessionId = this.mainService.sessionId;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  initForm(): void {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function
  updateAfterLoadSubtree(treeData: ExtendedTreeElement[]): void {}

  loadTreeData(password = ''): void {
    this.preparationOfParameters(password);

    this.loadingTree = true;
    this.loadInfo.emit({ loading: true });

    if (this.isUnix && this.resultPoint?.needDeepSync && !this.stepService.deepSyncSuccess$?.value) {
      this.loadInfo.emit({ loading: false });
      return this.runDeepSync(this.treeParams.params.Password || '');
    }

    this.stepService
      .getRemoteCommandData(this.treeParams, this.mainService.hid)
      .pipe(
        untilDestroyed(this),
        switchMap((data) => this.returnDataOrNextRequest(data))
      )
      .subscribe({
        next: (data) => {
          if (data?.data) {
            this.treeData = data.data.items.map((item, idx) => {
              const pathArray = item.path.split(this.joinForInput);
              const label = this.getLabelForRoot(item, pathArray);
              return {
                label,
                id: `disk-${idx}`,
                icon: this.getIconForRoot(item),
                checked: false,
                gotChildren: this.isVMRestore && (this.isNBF || !this.manually),
                expanded: false,
                path:
                  !this.treeParams.params.BunchId || this.isVMRestore || this.isRestoreDB || this.isUnix
                    ? item.path
                    : StepsHelpers.getRootPathWithJoin(item.path, this.joinForInput, this.isUnix, this.computerName),
                shown: true,
                hideCheckbox: this.isRestoreDB,
                restorePath:
                  this.isRestoreDB || (this.isVMRestore && !this.isNBF)
                    ? pathArray.slice(3).join(this.joinForInput)
                    : this.isVMRestore || this.isUnix
                      ? pathArray.slice(5).join(this.joinForInput)
                      : label
              };
            });
            this.updateTreeFromStepFormArray();
          } else this.treeData = [];

          if (this.resultPoint && this.resultPoint.needDeepSync) this.stepService.deepSyncSuccess$.next(true);

          this.offLoadAndGetSubtreeIfNeed();
        },
        error: (e) => {
          const code = +e?.error?.errorCode;

          this.loadInfo.emit({ loading: false });

          if (this.passwordErrorCodes.includes(code)) return this.showPasswordModal(WhatRunAfterPassword.main);
          if (DeepSyncCodes.includes(code)) return this.runDeepSync(this.treeParams.params.Password || '');

          this.treeData = [];
          this.loadingTree = false;
        }
      });
  }

  getSubtree(root: TreeElement): Observable<TreeElement[]> {
    const subtree$ = new BehaviorSubject<TreeElement[]>(null);

    this.updateTreeParamsBeforeRequest(root);

    this.stepService
      .getRemoteCommandData(this.treeParams, this.mainService.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => subtree$.next(this.getTreeDataFromRequest(data, root)),
        error: () => subtree$.next([])
      });

    return subtree$ as Observable<TreeElement[]>;
  }

  getLastCheckParent(item: ExtendedTreeElement): ExtendedTreeElement {
    if (!item.checked) return null;

    if (item.parent?.checked && !item.parent.indeterminate) return this.getLastCheckParent(item.parent);

    return item;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  changeTreeHandler(elem: ExtendedTreeElement): void {
    const event = this.isRestorePlan ? this.getLastCheckParent(elem) || elem : elem;
    const cbfVMRestorePoint = this.isVMRestore && !this.isNBF && !this.isDisks;
    let str = cbfVMRestorePoint ? event.path.split(this.joinForInput).slice(3).join(this.joinForInput) : this.getPathFromItem(event);
    let childrenShowedCount = 0;
    const replacedStr = this.getReplacedStr(str);
    const unixOnWindowsPC = !this.isUnix && windowsInvalidPath.test(str);

    if (!this.isUnix && str.length === 2) str += this.joinForInput;
    if (event.parent && cbfVMRestorePoint) {
      if (!event.checked) {
        this.stepForm.get('point').reset('');
        event.parent.children.forEach((ch) => (ch.checked = !ch.shown));
        event.parent.checked = true;
      } else {
        const strArr = str.split(this.joinForInput);
        const selectedPoint = strArr[strArr.length - 1];
        const pathArr = this.stepForm.get('folders').value.filter((s) => s === event.parent.restorePath);

        if (!pathArr?.includes(event.parent.restorePath)) {
          pathArr.push(event.parent.restorePath);
          this.setNewPathArray(pathArr);
        }

        this.stepForm.get('point').reset(selectedPoint);
      }
      this.resetPointsCheckState();
    }

    if (event.parent && !cbfVMRestorePoint) {
      const parenStrings = this.getParentStrAndLengthStr(event.parent);
      const everyShownChecked = event.checked && event.parent.children?.every((el) => !el.shown || el.checked);
      let childrenShowedCountParent = 0;
      let pathStrings = this.getFilteredPathStrings(
        this.stepForm.get('folders').value,
        event.isVersion,
        str,
        parenStrings,
        unixOnWindowsPC,
        replacedStr
      );

      event.children?.forEach((ch) => {
        if (ch.shown) childrenShowedCount++;
        if (!ch.shown) {
          if (everyShownChecked) ch.checked = true;
          else if (ch.checked) ch.checked = false;
        }
        if (ch.disableCheckbox && !event.checked) ch.disableCheckbox = false;
      });
      event.parent?.children?.forEach((c: MyTreeElements) => {
        if (c.shown) childrenShowedCountParent++;
        if (!c.shown && c.checked && (this.isUnix ? c.restorePath : c.path)) pathStrings.push(this.isUnix ? c.restorePath : c.path);
      });

      const needLoadMore = event.parent?.totalChildren > childrenShowedCountParent || (this.isLinux && event.parent?.totalChildren === -1);

      if (event.checked) {
        if (needLoadMore) {
          pathStrings.push(...this.stepForm.get('folders').value, str);
        } else {
          this.updatePathStringsFromAllCheckedChildren(event, pathStrings);
        }
        if (!event.isVersion && (!this.isRestorePlan || !everyShownChecked)) StepsHelpers.uncheckAllParent(event.parent, true, true);
        else if (event.parent?.parent && (!this.isRestorePlan || !everyShownChecked)) {
          StepsHelpers.uncheckAllParent(event.parent.parent, true, true);
        }
        if (event.children?.some((c: ExtendedTreeElement) => c.isVersion)) {
          event.children.forEach((child, i) => (child.checked = child.shown && i === 0));
        }
        if (event.totalChildren > childrenShowedCount) {
          event.children.forEach((ch) => (ch.disableCheckbox = true));
        }
      } else {
        if (
          (!event.parent.indeterminate ||
            pathStrings.some((s) => str.includes(s)) ||
            unixOnWindowsPC ||
            this.isUnixAndRootBranch(event.parent)) &&
          !event.parent.checked &&
          event.parent.parent
        ) {
          const indeterminateOrChecked = this.getIndeterminateOrCheckedParent(event.parent.parent, pathStrings);
          const newStr = this.getPathFromItem(indeterminateOrChecked);
          if (needLoadMore) {
            pathStrings = this.stepForm
              .get('folders')
              .value.filter(
                (s) =>
                  !StepsHelpers.equalStrings(s, str, this.isLinux) &&
                  StepsHelpers.pathNotIncludePath(this.isLinux, this.joinForInput, str, s)
              );
          } else {
            pathStrings = this.getFilteredPathStringsForUncheckEvent(pathStrings, newStr, this.getReplacedStr(newStr));
          }
          this.updateAllChildren(indeterminateOrChecked.children, pathStrings);
        }
        if (event.parent.indeterminate || event.isVersion) {
          pathStrings = pathStrings.filter((s) => {
            return this.isUnix || this.isRestoreDB
              ? !StepsHelpers.equalStrings(s, parenStrings.strParent, this.isUnix) &&
                  !StepsHelpers.equalStrings(s, parenStrings.replacedParent, this.isUnix)
              : !StepsHelpers.equalStrings(s, parenStrings.strParent, this.isUnix);
          });
        }
        if (!event.isVersion && event.parent.children?.some((c) => c.checked)) {
          const existVersion = event.parent?.children?.some(
            (c) =>
              c.shown &&
              c.checked &&
              pathStrings.some((s) => {
                const strFromChild = this.getPathFromItem(c);
                return this.isUnix
                  ? !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, strFromChild) &&
                      !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, this.getReplacedStr(strFromChild)) &&
                      this.testStrForVersion(s)
                  : !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, strFromChild) && this.testStrForVersion(s);
              })
          );
          if (!existVersion) {
            this.updatePathStringsFromAllCheckedChildren(event, pathStrings);
          }
          StepsHelpers.uncheckAllParent(event.parent, true, true, unixOnWindowsPC);
        } else {
          if (!this.isRestoreDB && !this.isVMRestore) this.resetAllHiddenChecked(event.parent);
          StepsHelpers.uncheckAllParent(event.parent, true, true, unixOnWindowsPC);
        }
      }

      this.setNewPathArray(pathStrings);
    }

    if (!event.parent) {
      const lowPath = str.toLowerCase();
      let pathStrings = this.isUnix
        ? this.stepForm
            .get('folders')
            .value.filter(
              (s) =>
                !s.includes(str) &&
                !str.includes(s) &&
                (this.joinForInput !== replacedStr ? !s.includes(replacedStr) && !replacedStr.includes(s) : s !== replacedStr)
            )
        : this.isVMRestore
          ? cbfVMRestorePoint && this.manually
            ? []
            : this.stepForm.get('folders').value.filter((s) => (cbfVMRestorePoint ? lowPath !== s.toLowerCase() : str !== s))
          : this.stepForm.get('folders').value.filter((s) => !s.includes(str) && !str.includes(s));

      if (!this.isDisks && this.isEdit && this.isVMRestore && this.isVirtual && !this.mainService.canChangeRestoreVMType) {
        pathStrings = [];
        this.treeData.forEach((item) => {
          if (item.id !== event.id) item.checked = false;
        });
      }

      event.children?.forEach((ch) => {
        if (ch.shown) childrenShowedCount++;
        if (ch.disableCheckbox && !event.checked) ch.disableCheckbox = false;
      });

      if (event.checked) {
        pathStrings.push(str);
        if (event.totalChildren > childrenShowedCount) {
          event.children.forEach((ch) => (ch.disableCheckbox = true));
        }
      }

      if (cbfVMRestorePoint && this.manually && (!event.checked || !event.children?.some((ch) => ch.checked))) {
        this.stepForm.get('point').reset('');
      }

      this.setNewPathArray(pathStrings);
    }

    if (!this.isDisks && event.parent && this.isRestoreDB) {
      this.treeData.forEach((el) => {
        if (el.id === event.parent.id) {
          el.children.forEach((ch) => {
            if (ch.id === event.id) {
              ch.checked = event.checked;
              ch.indeterminate = event.indeterminate;
            }
          });
        }
      });
    }

    if (!this.isDisks && (this.isVMRestore || this.isRestoreDatabase)) {
      this.stepService.treeData$.next({ folders: this.stepForm.get('folders').value, treeData: this.treeData });
    }

    if (this.isVMRestore && !this.isNBF) this.resetPointsCheckState();
  }

  resetAllHiddenChecked(parent: TreeElement): void {
    if (!parent) return;

    parent.children.forEach((el) => {
      if (!el.shown) {
        el.checked = false;
        el.indeterminate = false;
      }
    });

    this.resetAllHiddenChecked(parent.parent);
  }

  resetPointsCheckState(): void {
    const selectedPoint = this.stepForm.get('point').value;
    const path = this.joinForInput + selectedPoint;

    this.treeComponent.data.forEach((el) => {
      el.children?.forEach((ch) => {
        if (ch.checked && (!selectedPoint || !(ch as ExtendedTreeElement).path?.endsWith(path))) ch.checked = false;
      });
    });
  }

  setNewPathArray(pathStrings: string[]): void {
    pathStrings = Array.from(new Set(pathStrings));
    this.stepForm.get('folders').reset(pathStrings);

    this.value = { ...this.value, folders: pathStrings, valid: true };
  }

  updatePathStringsFromAllCheckedChildren(event: ExtendedTreeElement, pathStrings: string[]): void {
    event.parent.children.forEach((c) => {
      if (c.shown && c.checked) {
        const newPath =
          this.isDisks || this.isRestoreDB
            ? this.getPathFromItem(c)
            : this.isUnix
              ? (c as TreeElement & { restorePath?: string }).restorePath
              : StepsHelpers.getPathInStringFormatByOs(c, this.isUnix, false, false, this.isUnix);
        pathStrings.push(newPath);
      }
    });
  }

  /*
   * Block with second plan functions
   * **/
  updateTreeParamsBeforeRequest(root: Partial<ExtendedTreeElement>): void {
    this.treeParams.params.offset = root?.children
      ? root.children.reduce((acc, next) => {
          if (next.shown || next.shown !== false) acc++;
          return acc;
        }, 0)
      : 0;

    this.treeParams.params.path = root.path;
  }

  preparationOfParameters(password: string): void {
    this.treeParams.params.Password = password;
    this.treeParams.params.path = '';
    this.treeParams.params.BunchId = '';
    this.treeParams.params.RestoreSourcePrefix = '';
    const restorePointDateUtc = this.resultPoint?.date ? StepsHelpers.getNormalDate(this.resultPoint.date) : '';

    if (!this.isRestoreDB && restorePointDateUtc && this.resultPoint.path) {
      this.treeParams.params.path = this.resultPoint.path;
      return;
    }

    if (this.isRestoreDB || !restorePointDateUtc || !this.resultPoint.path) {
      this.treeParams.params.path = `${this.storageId}${this.joinForInput}${this.computerName || this.mainService.compName}${
        this.joinForInput
      }${this.bunchId}`;
      const generation = this.resultPoint?.generationId;

      if (generation) this.treeParams.params.path += `${this.joinForInput}${generation}`;
      if (restorePointDateUtc) this.treeParams.params.path += `${this.joinForInput}${restorePointDateUtc}`;

      return;
    }

    this.treeParams.params.BunchId = this.bunchId;
    this.treeParams.params.ConnectionId = this.storageId;
    this.treeParams.params.RestorePointDateUtc = restorePointDateUtc;
    this.treeParams.params.offset = 0;
    this.treeParams.params.RestoreSourcePrefix = this.computerName || this.mainService.compName;
  }

  getLabelForRoot(item: { displayName?: string }, pathArray: string[]): string {
    return this.isUnix && (pathArray[pathArray.length - 1] || item.displayName === this.joinForInput)
      ? item.displayName || pathArray[pathArray.length - 1].toUpperCase()
      : this.isVMRestore || this.isRestoreDB
        ? item.displayName
        : item.displayName + this.joinForInput;
  }

  getIconForRoot(item: { itemType?: ItemTypeEnum; type?: string }): string {
    if (item?.itemType === ItemTypeEnum.Container) return TreeBunchesIcons.ProxmoxContainer;
    if (item?.itemType === ItemTypeEnum.Machine) return this.returnMachineIconByType();

    return item?.type === 'Folder'
      ? TreeIconPath.Folder
      : this.isRestoreHyperV
        ? TreeBunchesIcons.HyperV
        : this.isRestoreVMWare
          ? TreeBunchesIcons.VmWare
          : this.isRestoreProxmox
            ? TreeBunchesIcons.Proxmox
            : TreeIconPath.Disk;
  }

  private returnMachineIconByType(): string {
    if (this.isRestoreHyperV) return TreeBunchesIcons.HyperV;
    if (this.isRestoreVMWare) return TreeBunchesIcons.VmWare;
    if (this.isRestoreProxmox) return TreeBunchesIcons.Proxmox;

    return TreeRestorePointsIconPath.Server;
  }

  returnDataOrNextRequest(data: any): Observable<any> {
    if (this.isVMRestore && data?.data?.items?.length) {
      const path = data.data.items[0].path;
      this.updateTreeParamsBeforeRequest({ label: '', id: '', path });

      return this.stepService.getRemoteCommandData(this.treeParams, this.mainService.hid).pipe(untilDestroyed(this));
    }

    return of(data);
  }

  updateTreeFromStepFormArray(): void {
    const formArray = this.stepForm.get('folders').value;

    this.treeData.forEach((item: TreeElement & { restorePath?: string; path?: string }) => {
      let path = this.isRestoreDB ? item?.path?.split(this.joinForInput)[3] : item?.restorePath || item?.path;
      const idx = (item.label as string)?.indexOf(this.joinForInput);

      if (this.isRestoreDB && idx !== -1 && path[idx] !== this.joinForInput) {
        const splitPath = path.split('');
        splitPath[idx] = this.joinForInput;
        path = splitPath.join('');
      }

      const formStrCurrent = formArray.some((incStr) => !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, incStr, path));

      item.checked = formArray.some((s) => StepsHelpers.equalStrings(path, s, this.isUnix));

      if (this.isUnix && path === this.joinForInput) {
        const newInc = StepsHelpers.findInNotRootFolder(formArray, this.treeData);
        item.indeterminate = !!newInc.length && !item.checked;
      } else {
        item.indeterminate = formStrCurrent && !item.checked;
      }
    });

    if (
      !this.isDisks &&
      (this.isEdit || this.mainService.initialFormData?.restoreSourceStep) &&
      (this.isVMRestore || this.isRestoreDatabase)
    ) {
      this.stepService.treeData$.next({ folders: [...formArray], treeData: this.treeData });
    }
  }

  showPasswordModal(whatRun: WhatRunAfterPassword): void {
    const data = {
      password: this.mainService.password.value || '',
      hid: this.mainService.hid,
      passwordInvalid: this.passwordInvalid,
      showHint: false,
      passwordRecoveryEnabled: this.allowedRecovery && this.isNBF && !this.isUnix,
      backupVersionUpdated: this.mainService.backupVersionUpdated,
      restorePoint: this.resultPoint
    };

    this.modal
      .openCustom(PasswordModalComponent, { data, collapsing: true })
      .then((result: { password: FormControl }) => {
        const passControl = result.password;
        this.mainService.password.next(passControl.value);
        this.passwordsForBunches[this.bunchId] = passControl.value;

        if (passControl.valid) {
          if (whatRun === WhatRunAfterPassword.deepSync) {
            this.runDeepSync(passControl.value);
          } else if (whatRun === WhatRunAfterPassword.statusDeepSync) this.getDeepSyncStatus(passControl.value);
          else {
            this.loadTreeData(passControl.value);
          }
        }
      })
      .catch(() => this.loadInfo.emit({ loading: false }));
  }

  getPathFromItem(item: TreeElement & { path?: string }): string {
    if (this.isRestoreDB) {
      const pathArray: string[] = item.path.split(this.joinForInput);
      let path: string = pathArray.slice(3).join(this.joinForInput);
      const idx = ((item.parent ? item.parent.label : item.label) as string)?.indexOf(this.joinForInput);

      if (this.isRestoreDB && idx !== -1 && path[idx] !== this.joinForInput) {
        const splitPath = path.split('');
        splitPath[idx] = this.joinForInput;
        path = splitPath.join('');
      }

      return path;
    }

    let restorePath = (item as TreeElement & { restorePath?: string }).restorePath;

    if (restorePath && !/([a-zA-Z]:)/.test(restorePath) && restorePath.startsWith(this.joinForInput + this.joinForInput)) {
      restorePath = restorePath.substring(2);
    }

    return !this.isUnix && !item.parent && !this.isVMRestore
      ? (item.label as string)
      : ((restorePath || item.path || item.label) as string);
  }

  getReplacedStr(str: string): string {
    return this.isUnix && str.startsWith('CBB_ROOT')
      ? str.startsWith('CBB_ROOT/')
        ? str.replace('CBB_ROOT/', this.joinForInput)
        : str.replace('CBB_ROOT', this.joinForInput)
      : str;
  }

  getParentStrAndLengthStr(parent: ExtendedTreeElement): {
    strParent: string;
    strParLen: number;
    replacedParent: string;
    replacedParentLen: number;
  } {
    let strParent = this.getPathFromItem(parent);
    const replacedParent = this.getReplacedStr(strParent);
    let strParLen = strParent.length;
    const replacedParentLen = replacedParent.length;
    if (!this.isUnix && strParLen === 2) {
      strParent += this.joinForInput;
      strParLen += 1;
    }

    return { strParent, strParLen, replacedParent, replacedParentLen };
  }

  getFilteredPathStrings(
    strings: string[],
    isVersion: boolean,
    str: string,
    parenStrings,
    unixOnWindowsPC = false,
    repStr?: string
  ): string[] {
    const needPrev = !unixOnWindowsPC
      ? parenStrings.strParLen <= 3
      : parenStrings.strParent.includes('CBB_ROOT\\') || (!this.isNBF && !this.isRestoreDB);

    let newStrings = strings.filter((s) => {
      const sLast = s[needPrev ? parenStrings.strParLen - 1 : parenStrings.strParLen];

      return (
        !s.includes(parenStrings.strParent) ||
        (isVersion
          ? StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, parenStrings.strParent) &&
            !StepsHelpers.equalStrings(s, parenStrings.strParent, this.isUnix)
          : sLast && sLast !== this.joinForInput) ||
        (!StepsHelpers.equalStrings(s, str, this.isUnix) &&
          StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, str) &&
          s.split(this.joinForInput).length - parenStrings.strParent.split(this.joinForInput).filter((s) => !!s).length > 1)
      );
    });
    if (this.isUnix && repStr) {
      newStrings = this.getFilteredPathStrings(newStrings, isVersion, repStr, {
        strParent: parenStrings.replacedParent,
        strParLen: parenStrings.replacedParentLen
      });
    }

    return newStrings;
  }

  isUnixAndRootBranch(item: ExtendedTreeElement): boolean {
    let parent: ExtendedTreeElement = item.parent || item;
    while (parent?.parent) {
      parent = parent.parent;
    }
    const path = this.getPathFromItem(parent);
    return (this.isUnix && path === this.joinForInput) || path === 'CBB_ROOT';
  }

  getIndeterminateOrCheckedParent(item: ExtendedTreeElement, stringsArray: string[]): ExtendedTreeElement {
    if (
      !item.parent ||
      ((item.indeterminate || !item.checked) &&
        (stringsArray.some((s) => StepsHelpers.equalStrings(s, this.getPathFromItem(item), this.isUnix)) || !item.parent))
    )
      return item;
    return this.getIndeterminateOrCheckedParent(item.parent, stringsArray);
  }

  getFilteredPathStringsForUncheckEvent(strings: string[], str: string, repStr?: string): string[] {
    let result = strings.filter(
      (s) => !StepsHelpers.equalStrings(s, str, this.isUnix) && StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, str, s)
    );
    if (this.isUnix && repStr) result = this.getFilteredPathStringsForUncheckEvent(result, repStr);
    return result;
  }

  updateAllChildren(children: ExtendedTreeElement[], stringsArray: string[]): void {
    children.forEach((c) => {
      if (c.shown && c.checked && (!this.isRestorePlan || !c.indeterminate)) {
        stringsArray.push(StepsHelpers.getPathInStringFormatByOs(c, this.isUnix, false, false, this.isUnix));
      }
      if (c.children?.length && (!this.isRestorePlan || !c.checked || c.indeterminate)) this.updateAllChildren(c.children, stringsArray);
    });
  }

  testStrForVersion(str: string): boolean {
    const strArr = str.split(this.joinForInput);
    const lastElem = strArr[strArr.length - 1];
    const existNormal = dateTimePointsReg.test(lastElem);
    const existCobble = dateTimePointsFormat.test(lastElem);
    return existNormal || existCobble;
  }

  getTreeDataFromRequest(data, root): ExtendedTreeElement[] {
    const existData = data && data.data && data.data.items.length;
    const folders = this.stepForm.get('folders').value;
    const checkedAll = root.checked && !root.indeterminate;
    const onlyOneDisk = this.isDisks && existData && data.data.items.length === 1;
    const settings = {
      rootTree: this.treeData,
      root,
      newInc: [],
      newExc: [],
      joinStr: this.joinForInput,
      sep: this.separator,
      restorePath: true,
      alt: existData && isNaN(+data.data.items[0].type),
      isManually: this.manually,
      isVMOrDB: this.isVMRestore || this.isRestoreDB,
      isCbfVM: this.isVMRestore && !this.isNBF,
      isHyperV: this.isRestoreHyperV,
      isProxmox: this.isRestoreProxmox,
      isUnix: this.isUnix,
      needSlice: this.isDisks && this.validVersionForGetDisks,
      disableCheckbox:
        checkedAll &&
        (onlyOneDisk ||
          root.disableCheckbox ||
          (data?.data?.totalCount && data.data.totalCount > data.data.items?.length + this.treeParams.params.offset) ||
          (this.isUnix && isNil(data.data.totalCount)))
    };

    this.updateRootAndDataItems(data, existData, settings, root);

    let treeData: ExtendedTreeElement[] = this.getTreeData(data, folders, settings);

    this.updateNewTreeElementsIfExistVersion(treeData, folders, root);

    const isVersionNeedUpdate = this.manually && treeData.length && treeData[0].isVersion;

    if (settings.isCbfVM && root.checked && !treeData.some((el) => el.checked)) {
      treeData.push({ label: '', id: root.id + this.separator, shown: false, checked: true });
    }
    if (isVersionNeedUpdate || settings.isCbfVM) root.onlyOneSelect = true;
    if (isVersionNeedUpdate) treeData = this.updateCheckStateForFileVersions(treeData, root);

    root.totalChildren = StepsHelpers.getTotalChildren(data?.data, treeData.length);

    if (root.children?.[0]?.disableCheckbox && treeData?.length + this.treeParams.params.offset >= root?.totalChildren) {
      root.children.forEach((el) => (el.disableCheckbox = false));
      treeData.forEach((el) => (el.disableCheckbox = false));
    }

    if (this.isDisks) this.updateAfterLoadSubtree(treeData);

    return treeData;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  updateNewTreeElementsIfExistVersion(treeData: ExtendedTreeElement[], folders: string[], root: TreeElement): void {
    const rootStr = this.getPathFromItem(root);
    const unixOnWindowsPC = !this.isUnix && windowsInvalidPath.test(rootStr);
    let needPushVoid =
      root.indeterminate &&
      !root.checked &&
      treeData.every((c) => c.checked && !c.indeterminate) &&
      !folders.some((s) => StepsHelpers.equalStrings(s, rootStr, this.isUnix));

    treeData.forEach((element) => {
      if (element.isFolder && element.checked && !element.indeterminate) {
        const str = this.getPathFromItem(element);
        let includeVersion = false;
        let needAllCheck = false;
        folders.some((s) => {
          if (!StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, str) && this.testStrForVersion(s)) {
            includeVersion = true;
            if (s.split(this.joinForInput)?.length - str.split(this.joinForInput)?.length === 2) {
              needAllCheck = true;
            }
          }
        });
        if (includeVersion) {
          this.needAllCheckChildren[element.id] = needAllCheck;
          element.checked = false;
          element.indeterminate = true;
        }
      } else if (!element.isVersion && this.needAllCheckChildren[root.id]) {
        element.checked = true;
        element.indeterminate = false;
        needPushVoid = true;
      }
    });

    if (needPushVoid) treeData.push({ label: '', id: root.id + this.separator, shown: false });
    if (this.isUnix || unixOnWindowsPC) {
      this.fixNotShownItemParent(root, folders);
      const foundRoot = this.getIndeterminateParent(root);
      if (foundRoot && foundRoot.children && root.parent)
        foundRoot.children.push({ label: '', id: root.parent.id + this.separator, shown: false });
    }
    if (this.needAllCheckChildren[root.id]) {
      this.needAllCheckChildren[root.id] = false;
    }
  }

  fixNotShownItemParent(root: ExtendedTreeElement, folders: string[]): void {
    let parent: ExtendedTreeElement = root;
    while (parent) {
      const str = this.getPathFromItem(parent);
      if (folders.some((s) => StepsHelpers.equalStrings(s, str, this.isUnix))) {
        const notShownIdx = parent?.children?.length ? parent.children.findIndex((c) => !c.shown && !c.checked) : -1;
        if (parent?.checked && !parent?.indeterminate && notShownIdx !== -1) {
          parent.children[notShownIdx].checked = true;
          parent = null;
        } else parent = parent.parent;
      } else parent = parent.parent;
    }
  }

  getIndeterminateParent(root: ExtendedTreeElement): ExtendedTreeElement {
    let parent: ExtendedTreeElement = root;
    let result: ExtendedTreeElement = null;
    while (parent) {
      if (!parent?.checked && parent?.indeterminate && !parent?.children?.some((c) => !c.shown)) {
        result = parent;
        parent = null;
      } else parent = parent.parent;
    }
    return result;
  }

  /*
   * Deep Sync section
   * **/
  runDeepSync(pass = ''): void {
    this.loadInfo.emit({ loading: true });
    const deepSyncParams = this.getDeepSyncParams(pass, true);
    this.stepService
      .getRemoteCommandData(deepSyncParams, this.mainService.hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.loadInfo.emit({ loading: false });
          if (result && result.data) {
            const syncDataFromRes = { deepSync: DeepSyncEnum[result.data as string], progress: 0, progressMessage: '' };
            if (syncDataFromRes.deepSync === DeepSyncEnum.InvalidPassword || syncDataFromRes.deepSync === DeepSyncEnum.NeedPassword) {
              if (syncDataFromRes.deepSync === DeepSyncEnum.InvalidPassword) this.passwordInvalid = true;
              this.showPasswordModal(WhatRunAfterPassword.deepSync);
            } else {
              this.getDeepSyncStatus(pass, syncDataFromRes);
            }
          }
        },
        error: () => this.loadInfo.emit({ loading: false })
      });
  }

  getDeepSyncStatus(pass = '', syncDate = { progress: 0, deepSync: DeepSyncEnum.InProgress, progressMessage: '' }): void {
    this.loadInfo.emit({ loading: true });
    const toast: Toast = { header: 'DeepSync process...', content: `${syncDate.progress}%`, type: MbsPopupType.info, delay: 5000 };

    if (syncDate.deepSync !== DeepSyncEnum.Error) {
      if (syncDate.deepSync === DeepSyncEnum.InvalidPassword || syncDate.deepSync === DeepSyncEnum.NeedPassword) {
        this.currentSyncData = null;
        if (syncDate.deepSync === DeepSyncEnum.InvalidPassword) this.passwordInvalid = true;
        this.loadInfo.emit({ loading: false });
        this.showPasswordModal(WhatRunAfterPassword.deepSync);
        toast.content =
          syncDate.deepSync === DeepSyncEnum.InvalidPassword ? syncDate.progressMessage || 'Invalid Password' : 'Need Password';
        toast.type = MbsPopupType.danger;
      } else if (syncDate.deepSync !== DeepSyncEnum.InProgressColdStorage && syncDate.deepSync !== DeepSyncEnum.InProgress) {
        this.currentSyncData = null;
        this.loadInfo.emit({ loading: false });
        toast.type = MbsPopupType.success;
        this.stepService.deepSyncSuccess$.next(true);
        this.loadTreeData();
      } else {
        toast.type = MbsPopupType.info;
        const getDeepSyncStatusParams = this.getDeepSyncParams(pass);
        this.stepService
          .getRemoteCommandData(getDeepSyncStatusParams, this.mainService.hid)
          .pipe(first(), debounceTime(800), untilDestroyed(this))
          .subscribe({
            next: (res) => {
              if (res) {
                if (res.data) {
                  const syncDataFromRes = {
                    deepSync: DeepSyncEnum[res.data.state as string],
                    progressMessage: res.data.progressMessage,
                    progress: res.data.state === 'Done' ? 100 : res.data.progress
                  };
                  this.getDeepSyncStatus(pass, syncDataFromRes);
                } else if (res.ok) {
                  const syncDataFromRes = { deepSync: DeepSyncEnum.InProgress, progress: 0, progressMessage: '' };
                  this.getDeepSyncStatus(pass, syncDataFromRes);
                }
              }
              this.loadInfo.emit({ loading: false });
            },
            error: (e) => {
              this.loadInfo.emit({ loading: false });
              const newToast = Object.assign({}, toast);
              newToast.content = (e && e.error && e.error.message) || unidentifiedErrorText;
              newToast.type = MbsPopupType.danger;
              this.toastService.toast(newToast);
              this.currentSyncData = null;
            }
          });
      }
    } else {
      this.loadInfo.emit({ loading: false });
      toast.content = 'Error';
      toast.type = MbsPopupType.warning;
    }
    if (
      !this.currentSyncData ||
      this.currentSyncData.deepSync !== syncDate.deepSync ||
      this.currentSyncData.progress !== syncDate.progress
    ) {
      this.toastService.toast(toast);
    }
    this.currentSyncData = syncDate;
  }

  private offLoadAndGetSubtreeIfNeed(): void {
    if (this.treeData.length === 1 && this.isRestoreDB) {
      this.getSubtree(this.treeData[0])
        .pipe(
          first((r) => !!r),
          untilDestroyed(this)
        )
        .subscribe({
          next: (children) => {
            this.treeData[0].children = children;
            this.treeData[0].gotChildren = true;
            this.treeData[0].expanded = true;
            this.treeData = cloneDeep(this.treeData);
            this.mainOffLoading();
          },
          error: () => this.mainOffLoading()
        });
    } else this.mainOffLoading();
  }

  private mainOffLoading(): void {
    this.loadingTree = false;
    this.loadInfo.emit({ loading: false });
  }

  private getDeepSyncParams(pass = '', isRun = false): ParamsForDeepSync {
    const newParams: ParamsForDeepSync = {
      agentType: 'backup',
      commandType: isRun ? 'RunDeepSync' : 'GetDeepSyncStatus',
      params: {
        connectionId: this.storageId,
        restoreSourcePrefix: this.computerName,
        bunchId: this.bunchId,
        restorePointDateUtc: StepsHelpers.getNormalDate(this.resultPoint.date)
      }
    };

    if (this.mainService?.sessionId) newParams.params.SessionId = this.mainService.sessionId;
    if (pass) newParams.params.Password = pass;

    return newParams;
  }

  private updateRootAndDataItems(data, existData: boolean, settings: GenerateTreeDataSettings, root: ExtendedTreeElement): void {
    if (existData) data.data.items = StepsHelpers.getSortedTreeItems(data.data.items, settings.alt);

    if (this.treeParams.params.offset) {
      const pathMap = {};
      data.data.items.forEach((el) => (pathMap[el.restorePath] = true));
      root.children = root.children.filter((ch: MyTreeElements) => ch.shown || !pathMap[ch.path]);
    }
  }

  private getTreeData(data, folders: string[], settings: GenerateTreeDataSettings): ExtendedTreeElement[] {
    if (this.isRestoreDB) return StepsHelpers.generateDBTreeData(data, folders, settings);
    if (this.isDisks) return StepsHelpers.generateDisksTreeData(data, this.stepForm.get('excludes').value, settings);

    return this.isVMRestore && this.manually
      ? StepsHelpers.generatePointsTreeData(data, folders, this.stepForm.get('point').value, settings)
      : StepsHelpers.generateTreeData(data, folders, [], settings);
  }

  private updateCheckStateForFileVersions(treeData: ExtendedTreeElement[], root): ExtendedTreeElement[] {
    const newTreeData = treeData.sort((a: ExtendedTreeElement, b: ExtendedTreeElement) => {
      const aPath = a.path.split(this.joinForInput);
      const bPath = b.path.split(this.joinForInput);
      return moment(aPath[aPath.length - 1], cobbledDateFormat).isAfter(moment(bPath[bPath.length - 1], cobbledDateFormat)) ? -1 : 1;
    });

    if (!newTreeData.some((td) => !td.checked) || (root.checked && newTreeData.every((td) => !td.checked))) {
      newTreeData.forEach((e, i) => (e.checked = (i === 0 && e.shown) || (!newTreeData[0].shown && i === 1 && e.shown)));
    }

    return newTreeData;
  }
}
